import type { Mesh, Raycaster, Vector2 } from 'three';

import type { DebugManager } from '../debug-manager.class';

/**
 * Enable hover detection for debug spheres
 * Sets up mouse event listener to detect when hovering over spheres
 */
export const enableHoverDetectionCfn = (
	self: DebugManager
): void => {
	if (self.hoverEnabled) return;

	const handler = (event: MouseEvent) => handleMouseMoveCfn(self, event);
	self.setMouseMoveHandler(handler);
	self.configAccess.renderer.domElement.addEventListener('mousemove', handler);
	self.setHoverEnabled(true);
};

/**
 * Disable hover detection
 */
export const disableHoverDetectionCfn = (
	self: DebugManager
): void => {
	if (!self.hoverEnabled) return;

	const handler = self.mouseMoveHandler;
	if (handler) {
		self.configAccess.renderer.domElement.removeEventListener('mousemove', handler);
		self.setMouseMoveHandler(null);
	}
	self.setHoverEnabled(false);

	// Hide any active tooltip
	const hoveredId = self.hoveredSphereId;
	if (hoveredId) {
		self.hideTooltip(hoveredId);
		self.setHoveredSphereId(null);
	}
};

/**
 * Handle mouse move for hover detection
 */
export const handleMouseMoveCfn = (
	self: DebugManager,
	event: MouseEvent
): void => {
	if (!self.visible || self.sphereCount === 0) {
		// Hide tooltip if debug is not visible
		const hoveredId = self.hoveredSphereId;
		if (hoveredId) {
			self.hideTooltip(hoveredId);
			self.setHoveredSphereId(null);
		}
		return;
	}

	const { camera, renderer } = self.configAccess;
	const rect = renderer.domElement.getBoundingClientRect();
	const raycaster = self.raycasterAccess;
	const mousePosition = self.mousePositionAccess;

	// Calculate normalized device coordinates (-1 to +1)
	mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
	mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

	// Perform raycasting against all spheres
	raycaster.setFromCamera(mousePosition, camera);
	const sphereArray = self.allSpheres;
	const intersects = raycaster.intersectObjects(sphereArray, false);

	if (intersects.length > 0) {
		// Find which sphere was hit
		const hitMesh = intersects[0].object as Mesh;
		const hitId = findSphereIdByMeshCfn(self, hitMesh);

		const currentHoveredId = self.hoveredSphereId;

		if (hitId && hitId !== currentHoveredId) {
			// Hide previous tooltip
			if (currentHoveredId) {
				self.hideTooltip(currentHoveredId);
			}

			// Show new tooltip
			self.setHoveredSphereId(hitId);
			self.showTooltip(hitId, event.clientX, event.clientY);
		} else if (hitId === currentHoveredId) {
			// Update tooltip position as mouse moves
			self.showTooltip(hitId, event.clientX, event.clientY);
		}
	} else {
		// No intersection - hide tooltip
		const hoveredId = self.hoveredSphereId;
		if (hoveredId) {
			self.hideTooltip(hoveredId);
			self.setHoveredSphereId(null);
		}
	}
};

/**
 * Find sphere ID by mesh reference
 */
export const findSphereIdByMeshCfn = (
	self: DebugManager,
	mesh: Mesh
): string | null => {
	for (const [id, sphere] of self.spheres) {
		if (sphere === mesh) {
			return id;
		}
	}
	return null;
};
